<template>
  <div id="overview">
    <Navbar current="Visão geral" />
    <Sidebar />
    <Container col="col-12">
      <Header
        :isSubHeader="false"
        title="Visão geral"
        :iconClass="'fa-solid fa-gauge-high text-primary'"
      >
        <template slot="button">
          <div class="d-flex align-items-center gap-3">
            <small class="text-muted d-none d-md-block">
              <template v-if="updatedAt">Atualizado {{ updatedLabel() }}</template>
            </small>
            <button
              class="btn btn-sm btn-white border"
              :disabled="loading"
              @click="load"
            >
              <i class="fa-solid fa-rotate me-1" :class="{ 'fa-spin': loading }"></i>
              Atualizar
            </button>
          </div>
        </template>
      </Header>

      <!-- Big numbers -->
      <div v-if="Object.keys(bigNumbers).length" class="big-row">
        <div v-for="c in bigCards" :key="c.key" class="big-card">
          <span class="big-icon"><i :class="c.icon"></i></span>
          <div class="big-info">
            <span class="big-value">{{ formatCount(bigNumbers[c.key]) }}</span>
            <span class="big-label">{{ c.label }}</span>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-6 text-muted">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p class="mb-0">Carregando...</p>
      </div>

      <!-- Erro -->
      <div v-else-if="errored" class="text-center py-6 text-muted">
        <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
        <p class="mb-2">Não foi possível carregar a visão geral.</p>
        <button class="btn btn-sm btn-outline-primary" @click="load">
          Tentar novamente
        </button>
      </div>

      <template v-else>
        <!-- CRÍTICOS -->
        <div class="ac-section">
          <div v-if="!critical.length" class="ac-empty">
            <i class="fa-solid fa-circle-check text-success me-2"></i>
            Tudo em dia por aqui. 🎉
          </div>

          <div v-else class="ac-grid">
            <div v-for="item in critical" :key="item.id" class="ac-card">
              <div class="ac-card-head">
                <span class="ac-icon danger">
                  <i :class="iconFor(item.id)"></i>
                </span>
                <span class="ac-count danger">{{ formatCount(item.count) }}</span>
                <span class="ac-card-label">{{ item.label }}</span>
              </div>

              <div class="ac-card-list">
                <div
                  v-for="row in previewRows(item)"
                  :key="row.key"
                  class="ac-row"
                  :class="{ clickable: !!row.to }"
                  @click="goToDetail(row)"
                >
                  <span v-if="row.avatar" class="ac-avatar">
                    <img v-if="row.avatar.img" :src="row.avatar.img" alt="" />
                    <span v-else>{{ row.avatar.initials }}</span>
                  </span>
                  <div class="ac-row-info">
                    <span class="ac-row-title">{{ row.title }}</span>
                    <span class="ac-row-sub">{{ row.sub }}</span>
                  </div>
                  <i v-if="row.to" class="fa-solid fa-chevron-right ac-row-arrow"></i>
                </div>
              </div>

              <button
                v-if="linkFor(item) && item.count > (item.preview || []).length"
                class="ac-see-all"
                @click="goToList(item)"
              >
                Ver todos os {{ formatCount(item.count) }}
                <i class="fa-solid fa-arrow-right ms-1"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- WARNINGS -->
        <div class="ac-section" v-if="warnings.length">
          <div class="ac-section-head">
            <span class="ac-section-icon warning">
              <i class="fa-solid fa-eye"></i>
            </span>
            <div>
              <h6 class="ac-section-title">Fica de olho</h6>
              <p class="ac-section-sub">Informativo e anomalias</p>
            </div>
          </div>

          <div class="ac-grid">
            <div
              v-for="item in warnings"
              :key="item.id"
              class="ac-warn"
              :class="{ spike: item.is_spike }"
            >
              <span class="ac-icon" :class="item.is_spike ? 'warning' : 'muted'">
                <i :class="item.is_spike ? 'fa-solid fa-fire' : iconFor(item.id)"></i>
              </span>
              <div class="ac-body">
                <span class="ac-count" :class="{ spike: item.is_spike }">
                  {{ formatCount(item.count) }}
                </span>
                <span class="ac-label">{{ item.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Container>
  </div>
</template>

<script>
import OverviewController from "./OverviewController";
export default OverviewController;
</script>

<style scoped>
.py-6 {
  padding-top: 4rem;
  padding-bottom: 4rem;
}

/* Big numbers */
.big-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 2rem;
}
.big-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(18, 38, 63, 0.04);
}
.big-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex: 0 0 46px;
  background-color: rgba(251, 164, 134, 0.16);
  color: var(--bs-primary, #fba486);
}
.big-info {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  min-width: 0;
}
.big-value {
  font-size: 1.7rem;
  font-weight: 800;
  color: #12263f;
  letter-spacing: -0.5px;
}
.big-label {
  font-size: 0.78rem;
  color: #8896a6;
  margin-top: 2px;
}

.ac-section {
  margin-bottom: 2rem;
}
.ac-section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.ac-section-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  flex: 0 0 40px;
}
.ac-section-icon.danger {
  background-color: #fde8ec;
  color: #e05c6e;
}
.ac-section-icon.warning {
  background-color: #fdf3d8;
  color: #d9a406;
}
.ac-section-title {
  margin: 0;
  font-weight: 700;
  color: #12263f;
  font-size: 1rem;
}
.ac-section-sub {
  margin: 0;
  font-size: 0.78rem;
  color: #95aac9;
}

/* Grid de cards */
.ac-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 16px;
  align-items: start;
}

/* Ícones */
.ac-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex: 0 0 44px;
}
.ac-icon.danger {
  background-color: #fde8ec;
  color: #e05c6e;
}
.ac-icon.warning {
  background-color: #fdf3d8;
  color: #d9a406;
}
.ac-icon.muted {
  background-color: #eef1f6;
  color: #8896a6;
}
.ac-count {
  font-size: 1.5rem;
  font-weight: 800;
  color: #12263f;
  letter-spacing: -0.5px;
  line-height: 1;
}
.ac-count.danger {
  color: #d6455b;
}
.ac-count.spike {
  color: #d9a406;
}
.ac-label {
  font-size: 0.85rem;
  color: #6b7a90;
}

/* Card de categoria crítica */
.ac-card {
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(18, 38, 63, 0.04);
  padding: 18px;
}
.ac-card-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 14px;
  margin-bottom: 6px;
  border-bottom: 1px solid #f0f2f6;
}
.ac-card-head .ac-count {
  flex: 0 0 auto;
}
.ac-card-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #3b4a5e;
  line-height: 1.25;
}

/* Linha da lista */
.ac-card-list {
  display: flex;
  flex-direction: column;
}
.ac-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f7fa;
}
.ac-row:last-child {
  border-bottom: none;
}
.ac-row.clickable {
  cursor: pointer;
}
.ac-row.clickable:hover .ac-row-title {
  color: #2c7be5;
}
.ac-avatar {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #5b6bb5;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
}
.ac-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ac-row-info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.ac-row-title {
  font-size: 0.87rem;
  font-weight: 600;
  color: #12263f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.15s;
}
.ac-row-sub {
  font-size: 0.75rem;
  color: #95aac9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ac-row-arrow {
  flex: 0 0 auto;
  color: #c3cddb;
  font-size: 0.72rem;
}
.ac-see-all {
  margin-top: 12px;
  width: 100%;
  border: 1px solid #e3e8ef;
  background-color: #f9fafc;
  border-radius: 10px;
  padding: 8px;
  color: #2c7be5;
  font-size: 0.82rem;
  font-weight: 600;
  transition: background-color 0.15s;
}
.ac-see-all:hover {
  background-color: #eef2f8;
}

/* Warnings */
.ac-warn {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background-color: #fff;
  border: 1px solid #edf0f5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(18, 38, 63, 0.04);
}
.ac-warn.spike {
  background-color: #fffaf0;
  border-color: #f6dfa6;
}
.ac-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  line-height: 1.2;
}

.ac-empty {
  padding: 20px;
  background-color: #f7f9fc;
  border-radius: 14px;
  color: #6b7a90;
  font-size: 0.9rem;
}
</style>
